// The first connector supplies the card logo. Product names between *markers*
// are emphasized on the card and inserted into the composer as plain text.
export type ChatExample = { connectors: readonly string[]; prompt: string };

export const chatExampleCategories = [
  { id: "tasks", label: "Tasks" },
  { id: "workflows", label: "Workflows" },
  { id: "apps", label: "Apps" },
  { id: "monitoring", label: "Monitoring" },
  { id: "connectors", label: "Connectors" },
] as const;
export type ChatExampleCategory = (typeof chatExampleCategories)[number]["id"];

// Building uses startWorkflow and the workflow editor’s code, connector,
// schedule, and HTTP-route tools. These are drafts; publishing remains a user
// decision. Apps use the React template with authenticated backend routes.
const workflows: ChatExample[] = [
  {
    connectors: ["linear"],
    prompt: "Build a workflow to flag possible duplicate *Linear* issues.",
  },
  {
    connectors: ["linear"],
    prompt: "Build a weekly report of unassigned *Linear* issues by team.",
  },
  {
    connectors: ["linear", "slack"],
    prompt: "Build a daily *Slack* reminder for blocked *Linear* issues.",
  },
  {
    connectors: ["linear", "notion"],
    prompt: "Build a *Notion* changelog from completed *Linear* issues.",
  },
  {
    connectors: ["slack"],
    prompt: "Build a weekly FAQ from questions in a *Slack* channel.",
  },
  {
    connectors: ["slack", "googleDocs"],
    prompt: "Build a standup summary in *Google Docs* from *Slack* updates.",
  },
  {
    connectors: ["slack", "googleSheets"],
    prompt:
      "Build a workflow that logs matching *Slack* messages to *Google Sheets*.",
  },
  {
    connectors: ["github"],
    prompt: "Build a *GitHub* issue labeler using rules I define.",
  },
  {
    connectors: ["github"],
    prompt: "Build a daily report of failed *GitHub Actions* runs.",
  },
  {
    connectors: ["github"],
    prompt: "Build a weekly CSV export of open *GitHub* issues.",
  },
  {
    connectors: ["jira"],
    prompt:
      "Build a workflow to flag *Jira* tickets missing acceptance criteria.",
  },
  {
    connectors: ["jira"],
    prompt: "Build a weekly cycle-time report from *Jira* issue history.",
  },
  {
    connectors: ["zendesk", "confluence"],
    prompt: "Build a *Zendesk* reply drafter using *Confluence* runbooks.",
  },
  {
    connectors: ["zendesk"],
    prompt: "Build a workflow to tag *Zendesk* tickets using routing rules.",
  },
  {
    connectors: ["salesforce"],
    prompt: "Build a workflow to flag duplicate *Salesforce* contacts.",
  },
  {
    connectors: ["salesforce", "asana"],
    prompt: "Build *Asana* onboarding tasks when a *Salesforce* deal closes.",
  },
  {
    connectors: ["hubSpot"],
    prompt: "Build a weekly report of *HubSpot* contacts missing key fields.",
  },
  {
    connectors: ["hubSpot", "slack"],
    prompt: "Build a *Slack* digest of *HubSpot* deals that changed stages.",
  },
  {
    connectors: ["stripe"],
    prompt: "Build a weekly report of canceled *Stripe* subscriptions.",
  },
  {
    connectors: ["stripe", "slack"],
    prompt: "Build a daily *Slack* digest of overdue *Stripe* invoices.",
  },
  {
    connectors: ["shopify", "slack"],
    prompt: "Build low-stock alerts from *Shopify* to *Slack*.",
  },
  {
    connectors: ["shopify"],
    prompt: "Build a weekly refunds report from *Shopify*.",
  },
  {
    connectors: ["airtable"],
    prompt: "Build a workflow to flag missing required fields in *Airtable*.",
  },
  {
    connectors: ["googleSheets"],
    prompt: "Build a workflow that imports uploaded CSVs into *Google Sheets*.",
  },
  {
    connectors: ["googleDocs", "airtable"],
    prompt: "Build a weekly *Google Docs* report from an *Airtable* view.",
  },
  {
    connectors: ["confluence"],
    prompt: "Build a workflow to check links in selected *Confluence* pages.",
  },
  {
    connectors: ["asana"],
    prompt: "Build a workflow that creates weekly checklists in *Asana*.",
  },
  {
    connectors: ["googleCalendar"],
    prompt: "Build a weekly meeting-load report from *Google Calendar*.",
  },
  {
    connectors: ["okta"],
    prompt: "Build a workflow that snapshots *Okta* group membership daily.",
  },
  {
    connectors: ["snowflake"],
    prompt: "Build a scheduled CSV export from a *Snowflake* query.",
  },
  {
    connectors: ["slack"],
    prompt: "Build a daily digest of a *Slack* channel.",
  },
  {
    connectors: ["github"],
    prompt: "Build a weekly release-notes workflow from *GitHub* PRs.",
  },
  {
    connectors: ["linear"],
    prompt: "Build a weekly cycle summary from *Linear*.",
  },
  {
    connectors: ["jira"],
    prompt: "Build a daily report of overdue *Jira* issues.",
  },
  {
    connectors: ["notion"],
    prompt: "Build a weekly digest of changes to a *Notion* database.",
  },
  {
    connectors: ["googleSheets"],
    prompt: "Build a daily data-quality check for *Google Sheets*.",
  },
  {
    connectors: ["zendesk"],
    prompt: "Build a daily summary of unresolved *Zendesk* tickets.",
  },
  {
    connectors: ["salesforce"],
    prompt: "Build a weekly pipeline report from *Salesforce*.",
  },
  {
    connectors: ["hubSpot"],
    prompt: "Build a workflow that flags stale *HubSpot* deals.",
  },
  {
    connectors: ["stripe"],
    prompt: "Build a daily report of failed *Stripe* payments.",
  },
  {
    connectors: ["sentry"],
    prompt: "Build a daily digest of new *Sentry* errors.",
  },
  {
    connectors: ["pagerDuty"],
    prompt: "Build a weekly incident review from *PagerDuty*.",
  },
  {
    connectors: ["datadog"],
    prompt: "Build a daily summary of triggered *Datadog* monitors.",
  },
  {
    connectors: ["asana"],
    prompt: "Build a weekly report of overdue *Asana* tasks.",
  },
  {
    connectors: ["airtable"],
    prompt: "Build a workflow to find duplicate *Airtable* records.",
  },
  {
    connectors: ["shopify"],
    prompt: "Build a daily sales and unfulfilled-orders report from *Shopify*.",
  },
  {
    connectors: ["googleCalendar"],
    prompt: "Build a daily agenda from *Google Calendar*.",
  },
  {
    connectors: ["gitlab"],
    prompt: "Build a daily digest of open *GitLab* merge requests.",
  },
  {
    connectors: ["serviceNow"],
    prompt: "Build a daily report of aging *ServiceNow* incidents.",
  },
  {
    connectors: ["okta"],
    prompt: "Build a daily summary of failed *Okta* sign-ins.",
  },
  {
    connectors: ["intercom"],
    prompt: "Build a weekly summary of *Intercom* support themes.",
  },
  {
    connectors: ["snowflake"],
    prompt: "Build a daily KPI report from a *Snowflake* query.",
  },
  {
    connectors: ["confluence"],
    prompt: "Build a report of *Confluence* pages needing review.",
  },
  {
    connectors: ["trello"],
    prompt: "Build a weekly digest of overdue *Trello* cards.",
  },
  {
    connectors: ["clickUp"],
    prompt: "Build a daily digest of overdue *ClickUp* tasks.",
  },
  {
    connectors: ["mondayDotCom"],
    prompt: "Build a weekly status report from a *monday.com* board.",
  },
  {
    connectors: ["slack", "github"],
    prompt: "Build a workflow that posts open *GitHub* PRs to *Slack* daily.",
  },
  {
    connectors: ["linear", "sentry"],
    prompt:
      "Build a workflow to turn new *Sentry* errors into *Linear* issues.",
  },
  {
    connectors: ["googleSheets", "stripe"],
    prompt: "Build a daily *Stripe* payments export to *Google Sheets*.",
  },
  {
    connectors: ["jira", "zendesk"],
    prompt:
      "Build a workflow to turn selected *Zendesk* tickets into *Jira* issues.",
  },
  {
    connectors: ["notion", "github"],
    prompt: "Build a workflow that saves *GitHub* release notes to *Notion*.",
  },
  {
    connectors: ["microsoftTeams", "pagerDuty"],
    prompt: "Build a daily *PagerDuty* incident digest for *Microsoft Teams*.",
  },
  {
    connectors: ["airtable", "hubSpot"],
    prompt: "Build a daily *HubSpot* deals export to *Airtable*.",
  },
  {
    connectors: ["slack", "googleCalendar"],
    prompt:
      "Build a workflow that posts my daily agenda to *Slack* from *Google Calendar*.",
  },
  {
    connectors: ["googleDocs", "linear"],
    prompt: "Build a weekly *Linear* status report in *Google Docs*.",
  },
  {
    connectors: ["asana", "slack"],
    prompt: "Build a workflow that posts overdue *Asana* tasks to *Slack*.",
  },
];

const apps: ChatExample[] = [
  {
    connectors: ["linear"],
    prompt: "Build a roadmap explorer from *Linear* projects.",
  },
  {
    connectors: ["linear"],
    prompt:
      "Build a workload dashboard from *Linear* assignees and priorities.",
  },
  {
    connectors: ["linear"],
    prompt: "Build a customer-feedback inbox backed by *Linear*.",
  },
  {
    connectors: ["github"],
    prompt: "Build an issue board for a *GitHub* repository.",
  },
  {
    connectors: ["github"],
    prompt: "Build a release-notes app that compares *GitHub* tags.",
  },
  {
    connectors: ["github"],
    prompt: "Build a failed-build explorer for *GitHub Actions*.",
  },
  {
    connectors: ["slack"],
    prompt:
      "Build a *Slack* announcement composer with a preview before sending.",
  },
  {
    connectors: ["notion"],
    prompt: "Build an events directory backed by *Notion*.",
  },
  {
    connectors: ["notion"],
    prompt: "Build a retrospective app that saves feedback to *Notion*.",
  },
  {
    connectors: ["googleSheets"],
    prompt: "Build a budget planner backed by *Google Sheets*.",
  },
  {
    connectors: ["googleSheets"],
    prompt: "Build a team rota editor backed by *Google Sheets*.",
  },
  {
    connectors: ["airtable"],
    prompt: "Build an equipment checkout app backed by *Airtable*.",
  },
  {
    connectors: ["airtable"],
    prompt: "Build a feedback voting board backed by *Airtable*.",
  },
  {
    connectors: ["airtable"],
    prompt: "Build a resource booking app backed by *Airtable*.",
  },
  {
    connectors: ["zendesk"],
    prompt: "Build a support workload dashboard from *Zendesk*.",
  },
  {
    connectors: ["zendesk", "confluence"],
    prompt: "Build a reply-drafting desk for *Zendesk* using *Confluence*.",
  },
  {
    connectors: ["salesforce"],
    prompt: "Build a searchable account and contact browser for *Salesforce*.",
  },
  {
    connectors: ["salesforce"],
    prompt: "Build an account-planning workspace backed by *Salesforce*.",
  },
  {
    connectors: ["hubSpot"],
    prompt: "Build a lead qualification workspace backed by *HubSpot*.",
  },
  {
    connectors: ["stripe"],
    prompt: "Build a *Stripe* subscription explorer with status filters.",
  },
  {
    connectors: ["stripe"],
    prompt: "Build an unpaid-invoice dashboard from *Stripe*.",
  },
  {
    connectors: ["shopify"],
    prompt: "Build a fulfillment queue from *Shopify* orders.",
  },
  {
    connectors: ["shopify"],
    prompt: "Build a product and inventory browser for *Shopify*.",
  },
  {
    connectors: ["sentry"],
    prompt: "Build a *Sentry* issue detail app with stack traces and trends.",
  },
  {
    connectors: ["datadog"],
    prompt: "Build a metrics explorer using *Datadog* queries I choose.",
  },
  {
    connectors: ["serviceNow"],
    prompt: "Build an incident intake form backed by *ServiceNow*.",
  },
  {
    connectors: ["trello"],
    prompt: "Build a due-date calendar from *Trello* cards.",
  },
  {
    connectors: ["googleCalendar"],
    prompt: "Build a room booking app using *Google Calendar* resources.",
  },
  {
    connectors: ["confluence"],
    prompt:
      "Build a *Confluence* page review queue with age and owner filters.",
  },
  {
    connectors: ["gitlab"],
    prompt: "Build a project release explorer for *GitLab*.",
  },
  {
    connectors: ["linear"],
    prompt: "Build a triage board for *Linear* issues.",
  },
  {
    connectors: ["github"],
    prompt: "Build a *GitHub* PR review dashboard with filters.",
  },
  { connectors: ["jira"], prompt: "Build a sprint dashboard from *Jira*." },
  {
    connectors: ["slack"],
    prompt: "Build a searchable viewer for a *Slack* channel.",
  },
  {
    connectors: ["notion"],
    prompt: "Build a searchable directory from a *Notion* database.",
  },
  {
    connectors: ["googleSheets"],
    prompt: "Build an interactive dashboard from a *Google Sheets* tracker.",
  },
  {
    connectors: ["airtable"],
    prompt: "Build an intake form that saves requests to *Airtable*.",
  },
  {
    connectors: ["zendesk"],
    prompt: "Build a *Zendesk* ticket dashboard with status and owner filters.",
  },
  {
    connectors: ["salesforce"],
    prompt: "Build a *Salesforce* pipeline dashboard with deal details.",
  },
  {
    connectors: ["hubSpot"],
    prompt: "Build a *HubSpot* deal board with stage filters.",
  },
  {
    connectors: ["stripe"],
    prompt: "Build a *Stripe* payments dashboard with date filters.",
  },
  { connectors: ["shopify"], prompt: "Build a *Shopify* order lookup app." },
  {
    connectors: ["pagerDuty"],
    prompt: "Build an on-call and incident dashboard from *PagerDuty*.",
  },
  {
    connectors: ["sentry"],
    prompt: "Build a *Sentry* error explorer with project filters.",
  },
  {
    connectors: ["datadog"],
    prompt: "Build a status dashboard for *Datadog* monitors.",
  },
  { connectors: ["asana"], prompt: "Build a project tracker from *Asana*." },
  {
    connectors: ["googleCalendar"],
    prompt: "Build a team agenda app from *Google Calendar*.",
  },
  {
    connectors: ["confluence"],
    prompt: "Build a searchable runbook directory from *Confluence*.",
  },
  {
    connectors: ["gitlab"],
    prompt: "Build a *GitLab* merge-request and pipeline dashboard.",
  },
  {
    connectors: ["serviceNow"],
    prompt: "Build an incident triage dashboard from *ServiceNow*.",
  },
  {
    connectors: ["okta"],
    prompt: "Build a read-only *Okta* user and group directory.",
  },
  {
    connectors: ["intercom"],
    prompt: "Build an *Intercom* conversation dashboard with team filters.",
  },
  {
    connectors: ["snowflake"],
    prompt: "Build a dashboard from a *Snowflake* query, with CSV export.",
  },
  {
    connectors: ["trello"],
    prompt: "Build a cross-board task viewer for *Trello*.",
  },
  {
    connectors: ["clickUp"],
    prompt: "Build a task dashboard from *ClickUp* with due-date filters.",
  },
  {
    connectors: ["mondayDotCom"],
    prompt: "Build a project status dashboard from *monday.com*.",
  },
  {
    connectors: ["googleSheets"],
    prompt: "Build an expense intake app backed by *Google Sheets*.",
  },
  {
    connectors: ["linear"],
    prompt: "Build a bug-report form that creates *Linear* issues.",
  },
  {
    connectors: ["jira"],
    prompt: "Build a request form that creates *Jira* tickets.",
  },
  {
    connectors: ["airtable"],
    prompt: "Build an inventory browser from *Airtable*.",
  },
  {
    connectors: ["github", "linear"],
    prompt:
      "Build a delivery dashboard combining *GitHub* PRs and *Linear* issues.",
  },
  {
    connectors: ["salesforce", "zendesk"],
    prompt:
      "Build an account view with *Salesforce* deals and *Zendesk* tickets.",
  },
  {
    connectors: ["sentry", "pagerDuty"],
    prompt:
      "Build an incident console with *Sentry* errors and *PagerDuty* incidents.",
  },
  {
    connectors: ["shopify", "googleSheets"],
    prompt:
      "Build a *Shopify* sales dashboard with targets from *Google Sheets*.",
  },
  {
    connectors: ["notion", "slack"],
    prompt:
      "Build a request portal backed by *Notion* with *Slack* notifications.",
  },
  {
    connectors: ["googleDocs"],
    prompt: "Build a searchable reader for *Google Docs* documents I choose.",
  },
];

// All Monitoring prompts use read-only tenant tools, scoped to visible data.
// get_workflows_report/list_runs/get_execution_logs: health and diagnosis.
// get_ai_report: tokens, requests, and estimated spend (up to 90 days).
// get_billable_usage_report: calendar-month Tines Units, not forecasts.
// get_spaces_report: current storage, not historical growth or file contents.
// get_dependencies_report: declared packages, not vulnerability findings.
// get_connectors_report: requests/errors, not latency or raw request logs.
const monitoring: ChatExample[] = [
  {
    connectors: [],
    prompt: "Which paused workflows ran in the last seven days?",
  },
  {
    connectors: [],
    prompt: "Compare workflow failure rates across spaces this week.",
  },
  {
    connectors: [],
    prompt: "Compare recent live and draft executions for a workflow I choose.",
  },
  {
    connectors: [],
    prompt: "Which step failed in a workflow’s latest unsuccessful run?",
  },
  {
    connectors: [],
    prompt: "Find the slowest recent executions in a workflow I choose.",
  },
  {
    connectors: [],
    prompt: "How has the daily workflow failure rate changed this month?",
  },
  {
    connectors: [],
    prompt: "Compare input and output AI tokens by model this week.",
  },
  {
    connectors: [],
    prompt: "How many AI tokens came from cache reads and writes this week?",
  },
  {
    connectors: [],
    prompt: "Compare estimated AI spend per request across models.",
  },
  {
    connectors: [],
    prompt: "Which workflows made the most AI requests this month?",
  },
  { connectors: [], prompt: "Break down estimated AI spend by usage source." },
  {
    connectors: [],
    prompt: "Which models and connectors have unpriced AI usage?",
  },
  {
    connectors: [],
    prompt: "Show Tines Units usage over the last three complete months.",
  },
  {
    connectors: [],
    prompt: "Which workflows had the largest monthly increase in Tines Units?",
  },
  {
    connectors: [],
    prompt:
      "Which spaces use the largest share of their Tines Units for storage?",
  },
  {
    connectors: [],
    prompt:
      "How much of last month’s workflow usage came from the top five workflows?",
  },
  {
    connectors: [],
    prompt: "Show current storage beside this month’s storage Tines Units.",
  },
  {
    connectors: [],
    prompt: "Which spaces account for 80% of current storage?",
  },
  {
    connectors: [],
    prompt: "Which of my visible spaces currently report zero storage?",
  },
  {
    connectors: [],
    prompt: "What share of current storage belongs to a space I choose?",
  },
  {
    connectors: [],
    prompt: "Make a table of declared package versions across workflows.",
  },
  {
    connectors: [],
    prompt: "Compare Python and Node package declarations across workflows.",
  },
  {
    connectors: [],
    prompt:
      "Which declared dependencies use version ranges instead of exact pins?",
  },
  {
    connectors: [],
    prompt: "Find workflows declaring a specific package version I provide.",
  },
  {
    connectors: [],
    prompt: "Compare this week’s connector request volume with last week.",
  },
  {
    connectors: [],
    prompt: "Which connectors had traffic but no reported errors this week?",
  },
  {
    connectors: [],
    prompt: "Find each connector’s busiest day in the last 30 days.",
  },
  {
    connectors: [],
    prompt: "Break down connector errors for a workflow I choose.",
  },
  {
    connectors: [],
    prompt: "Break down non-workflow connector traffic by source.",
  },
  {
    connectors: [],
    prompt: "Do failing workflows also have connector errors this week?",
  },
  {
    connectors: [],
    prompt: "Which workflows have failed in the last seven days?",
  },
  {
    connectors: [],
    prompt: "Which workflows used the most AI tokens this week?",
  },
  { connectors: [], prompt: "Break down this month’s Tines Units by space." },
  {
    connectors: [],
    prompt: "Which spaces are using the most storage right now?",
  },
  {
    connectors: [],
    prompt: "Which packages are declared by the most workflows?",
  },
  { connectors: [], prompt: "Which connectors had the most errors this week?" },
  {
    connectors: [],
    prompt: "Show my busiest workflows over the last 30 days.",
  },
  { connectors: [], prompt: "Break down this week’s AI usage by model." },
  {
    connectors: [],
    prompt: "Which workflows used the most Tines Units last month?",
  },
  {
    connectors: [],
    prompt: "How is current storage split across my visible spaces?",
  },
  {
    connectors: [],
    prompt: "Which packages have multiple declared versions across workflows?",
  },
  {
    connectors: [],
    prompt: "Which workflows make the most connector requests?",
  },
  {
    connectors: [],
    prompt: "Compare daily workflow run counts over the last two weeks.",
  },
  {
    connectors: [],
    prompt: "Compare estimated AI spend this week with last week.",
  },
  {
    connectors: [],
    prompt: "Compare Tines Units across the last two complete months.",
  },
  {
    connectors: [],
    prompt: "What share of current storage is in the largest space?",
  },
  { connectors: [], prompt: "Which workflows declare the most packages?" },
  {
    connectors: [],
    prompt: "Compare daily connector error rates over the last two weeks.",
  },
  {
    connectors: [],
    prompt: "Help me investigate the logs from a recent failed run.",
  },
  {
    connectors: [],
    prompt: "Which spaces account for the most estimated AI spend?",
  },
  {
    connectors: [],
    prompt: "Compare storage and other Tines Units usage by space last month.",
  },
  {
    connectors: [],
    prompt: "List my visible spaces from largest to smallest by storage.",
  },
  {
    connectors: [],
    prompt: "Where could we standardize declared package versions?",
  },
  {
    connectors: [],
    prompt: "Which connectors have the highest error rates this week?",
  },
  {
    connectors: [],
    prompt: "Which live workflows had no runs in the last 30 days?",
  },
  {
    connectors: [],
    prompt: "How is AI usage split between chats and workflows?",
  },
  {
    connectors: [],
    prompt: "How much of last month’s Tines Units came from storage?",
  },
  {
    connectors: [],
    prompt: "Compare current storage in team spaces and my personal space.",
  },
  { connectors: [], prompt: "Which workflows declare a package I specify?" },
  {
    connectors: [],
    prompt: "How is connector traffic split across spaces this week?",
  },
  {
    connectors: [],
    prompt: "Which spaces had the biggest rise in runs this week?",
  },
  {
    connectors: [],
    prompt: "Is any of this month’s reported AI usage unpriced?",
  },
  {
    connectors: [],
    prompt: "Which spaces drove the change in Tines Units last month?",
  },
  {
    connectors: [],
    prompt: "How much storage do my visible spaces use in total?",
  },
  {
    connectors: [],
    prompt: "Summarize declared dependencies by package source.",
  },
  {
    connectors: [],
    prompt: "How much connector traffic happened outside workflows?",
  },
];

// Discovery and saved status: searchConnectors with usable/readable access.
// Usage and attachments: get_connectors_report/list_workflows/get_workflow.
// Workflow-backed API shapes: connectChatToApp + getConnectorDocs only.
// Service probes: connectChatToApp + execute, using read-only requests.
// Chat cannot call test_connector, rotate secrets, or edit connector access.
const connectorExamples: ChatExample[] = [
  {
    connectors: ["linear"],
    prompt: "Read a sample *Linear* issue and explain its fields.",
  },
  {
    connectors: ["linear"],
    prompt: "Which account does my *Linear* connector authenticate as?",
  },
  {
    connectors: ["linear"],
    prompt: "Which of my visible workflows use *Linear*?",
  },
  {
    connectors: ["linear"],
    prompt: "Explain my *Linear* connector’s latest saved test result.",
  },
  {
    connectors: ["linear"],
    prompt:
      "Which issue statuses and labels are available in my *Linear* team?",
  },
  {
    connectors: ["github"],
    prompt: "Check the API rate-limit status of my *GitHub* connector.",
  },
  {
    connectors: ["slack"],
    prompt: "Which workspace and identity does my *Slack* connector use?",
  },
  {
    connectors: ["googleSheets"],
    prompt: "Read a sample range from *Google Sheets* and explain its values.",
  },
  {
    connectors: ["notion"],
    prompt: "Describe the properties in a *Notion* database I choose.",
  },
  {
    connectors: ["airtable"],
    prompt: "Which *Airtable* bases can my connector access?",
  },
  {
    connectors: ["salesforce"],
    prompt: "Which objects can my *Salesforce* connector query?",
  },
  {
    connectors: ["hubSpot"],
    prompt: "Describe the contact properties exposed by *HubSpot*.",
  },
  {
    connectors: ["zendesk"],
    prompt: "Which ticket forms are available through *Zendesk*?",
  },
  {
    connectors: ["stripe"],
    prompt: "Which *Stripe* account is this connector linked to?",
  },
  {
    connectors: ["shopify"],
    prompt: "Which inventory locations can my *Shopify* connector see?",
  },
  {
    connectors: ["googleCalendar"],
    prompt: "Read today’s free/busy availability through *Google Calendar*.",
  },
  {
    connectors: ["gitlab"],
    prompt: "Which user does my *GitLab* connector authenticate as?",
  },
  {
    connectors: ["serviceNow"],
    prompt: "Read a sample *ServiceNow* incident and explain its fields.",
  },
  {
    connectors: ["okta"],
    prompt: "Which applications are visible through my *Okta* connector?",
  },
  {
    connectors: ["snowflake"],
    prompt: "Describe the columns in a *Snowflake* table I choose.",
  },
  {
    connectors: ["intercom"],
    prompt: "Which support teams are visible through *Intercom*?",
  },
  {
    connectors: ["trello"],
    prompt: "Which boards can my *Trello* connector read?",
  },
  {
    connectors: ["asana"],
    prompt: "Show the custom fields in an *Asana* project I choose.",
  },
  {
    connectors: ["microsoftTeams"],
    prompt: "Which teams can my *Microsoft Teams* connector access?",
  },
  {
    connectors: ["clickUp"],
    prompt: "Which spaces can my *ClickUp* connector access?",
  },
  {
    connectors: ["mondayDotCom"],
    prompt: "Explain the columns on a *monday.com* board I choose.",
  },
  {
    connectors: ["pagerDuty"],
    prompt: "Which on-call schedules can my *PagerDuty* connector read?",
  },
  {
    connectors: ["datadog"],
    prompt: "Which metric names are available through my *Datadog* connector?",
  },
  {
    connectors: ["googleDocs"],
    prompt: "Read a *Google Docs* document’s outline through my connector.",
  },
  {
    connectors: [],
    prompt:
      "Try a documented read-only endpoint on a workflow-backed connector.",
  },
  {
    connectors: ["slack"],
    prompt: "Which *Slack* channels can this connector read?",
  },
  {
    connectors: ["github"],
    prompt: "Which *GitHub* repositories can this connector access?",
  },
  {
    connectors: ["linear"],
    prompt: "Which *Linear* teams are available through my connector?",
  },
  {
    connectors: ["jira"],
    prompt: "Which *Jira* projects can this connector see?",
  },
  {
    connectors: ["notion"],
    prompt: "Which *Notion* pages are shared with this connector?",
  },
  {
    connectors: ["googleCalendar"],
    prompt: "Which *Google Calendar* calendars can this connector read?",
  },
  {
    connectors: ["airtable"],
    prompt: "Show the tables and fields in an *Airtable* base I choose.",
  },
  {
    connectors: ["salesforce"],
    prompt: "Describe the fields on a *Salesforce* object I choose.",
  },
  {
    connectors: ["hubSpot"],
    prompt: "Show the deal stages available through my *HubSpot* connector.",
  },
  {
    connectors: ["zendesk"],
    prompt: "Which ticket fields are available through *Zendesk*?",
  },
  {
    connectors: ["stripe"],
    prompt: "Read a sample *Stripe* payment and explain its fields.",
  },
  {
    connectors: ["shopify"],
    prompt: "Read a sample *Shopify* order and explain its fields.",
  },
  {
    connectors: ["googleSheets"],
    prompt: "Show the tabs and column headers in a *Google Sheets* file.",
  },
  {
    connectors: ["sentry"],
    prompt: "Which *Sentry* projects can my connector access?",
  },
  {
    connectors: ["pagerDuty"],
    prompt: "Which *PagerDuty* services are visible to my connector?",
  },
  {
    connectors: ["asana"],
    prompt: "Which *Asana* workspaces can my connector access?",
  },
  {
    connectors: ["gitlab"],
    prompt: "Which *GitLab* projects can my connector access?",
  },
  {
    connectors: ["confluence"],
    prompt: "Which *Confluence* spaces can this connector read?",
  },
  {
    connectors: ["okta"],
    prompt: "Read a sample *Okta* group and explain its fields.",
  },
  {
    connectors: ["datadog"],
    prompt: "Show the monitor types available in my *Datadog* account.",
  },
  { connectors: [], prompt: "Which connectors are available to me?" },
  {
    connectors: [],
    prompt: "Which of my connectors have a failed saved connection test?",
  },
  {
    connectors: [],
    prompt: "Which connectors have no saved connection-test result?",
  },
  { connectors: [], prompt: "Show my most recently added connectors." },
  { connectors: [], prompt: "Who created the connectors I can access?" },
  {
    connectors: [],
    prompt: "Which connectors use the owner’s account or each visitor’s?",
  },
  {
    connectors: [],
    prompt: "Find a connector that can call an API URL I give you.",
  },
  {
    connectors: [],
    prompt: "Help me find the setup link for a connector I need.",
  },
  {
    connectors: [],
    prompt: "Which of my visible workflows use a connector I choose?",
  },
  {
    connectors: [],
    prompt: "Which connectors are attached to a workflow I choose?",
  },
  {
    connectors: [],
    prompt: "Explain the saved test error on a connector I choose.",
  },
  {
    connectors: [],
    prompt: "Show this week’s request and error counts for a connector.",
  },
  {
    connectors: [],
    prompt: "Explain the endpoints exposed by a workflow-backed connector.",
  },
  {
    connectors: [],
    prompt: "Show the request shape for a workflow-backed connector endpoint.",
  },
  {
    connectors: [],
    prompt: "Help me make a read-only API request through a connector.",
  },
  {
    connectors: [],
    prompt: "Suggest an automation using the connectors available to me.",
  },
];

// Tasks use connectChatToApp + execute for authenticated connector requests.
// Availability of individual data and writes follows the connector’s scopes.
const tasks: ChatExample[] = [
  { connectors: ["slack"], prompt: "What did I miss in *Slack* today?" },
  {
    connectors: ["slack"],
    prompt: "Summarize today’s threads in my *Slack* #incidents channel.",
  },
  {
    connectors: ["slack"],
    prompt: "Who mentioned me in *Slack* this morning, and what do they need?",
  },
  {
    connectors: ["github"],
    prompt: "What *GitHub* pull requests are waiting on my review?",
  },
  {
    connectors: ["github"],
    prompt: "Summarize the *GitHub* issues assigned to me this week.",
  },
  {
    connectors: ["github"],
    prompt: "Which of my *GitHub* pull requests have failing checks?",
  },
  {
    connectors: ["jira"],
    prompt: "Which *Jira* issues are assigned to me right now?",
  },
  { connectors: ["jira"], prompt: "What’s blocking my current *Jira* sprint?" },
  {
    connectors: ["googleCalendar"],
    prompt: "What’s on my *Google Calendar* today, and what should I prep for?",
  },
  {
    connectors: ["googleCalendar"],
    prompt: "When’s my next free hour on *Google Calendar*?",
  },
  {
    connectors: ["notion"],
    prompt: "Find our latest planning notes in *Notion*.",
  },
  {
    connectors: ["notion"],
    prompt: "What action items are still open in my *Notion* docs?",
  },
  {
    connectors: ["linear"],
    prompt: "What’s assigned to me in *Linear* this cycle?",
  },
  {
    connectors: ["linear"],
    prompt: "Summarize what my team shipped in *Linear* last week.",
  },
  {
    connectors: ["salesforce"],
    prompt: "Which of my *Salesforce* deals are closing this month?",
  },
  {
    connectors: ["salesforce"],
    prompt: "Which *Salesforce* accounts haven’t I followed up with?",
  },
  {
    connectors: ["hubSpot"],
    prompt: "Which *HubSpot* leads should I follow up with today?",
  },
  {
    connectors: ["zendesk"],
    prompt: "What are my highest-priority open *Zendesk* tickets?",
  },
  {
    connectors: ["zendesk"],
    prompt: "Summarize the *Zendesk* tickets breaching SLA today.",
  },
  {
    connectors: ["intercom"],
    prompt: "What are *Intercom* customers asking about most this week?",
  },
  {
    connectors: ["stripe"],
    prompt: "How much revenue did *Stripe* bring in this week?",
  },
  {
    connectors: ["stripe"],
    prompt: "Which *Stripe* subscriptions failed to renew recently?",
  },
  {
    connectors: ["datadog"],
    prompt: "Are there any active *Datadog* alerts I should know about?",
  },
  {
    connectors: ["pagerDuty"],
    prompt: "What *PagerDuty* incidents are open, and who’s on call?",
  },
  {
    connectors: ["sentry"],
    prompt: "What new *Sentry* errors have shown up since yesterday?",
  },
  {
    connectors: ["gitlab"],
    prompt: "Which *GitLab* merge requests are waiting on me?",
  },
  {
    connectors: ["figma"],
    prompt: "Summarize the version history of a *Figma* file I choose.",
  },
  {
    connectors: ["asana"],
    prompt: "Which *Asana* tasks are due for me this week?",
  },
  {
    connectors: ["confluence"],
    prompt: "Find our on-call runbook in *Confluence*.",
  },
  {
    connectors: ["googleSheets"],
    prompt: "Summarize the latest numbers in my *Google Sheets* tracker.",
  },
  {
    connectors: ["googleDocs"],
    prompt: "Summarize a *Google Docs* document I choose.",
  },
  {
    connectors: ["shopify"],
    prompt: "How are today’s *Shopify* orders and sales tracking?",
  },
  {
    connectors: ["zoom"],
    prompt: "Summarize an available *Zoom* recording transcript.",
  },
  {
    connectors: ["airtable"],
    prompt: "Summarize the records in an *Airtable* view.",
  },
  {
    connectors: ["microsoftTeams"],
    prompt: "What did I miss in *Microsoft Teams* today?",
  },
  {
    connectors: ["serviceNow"],
    prompt: "What *ServiceNow* incidents are assigned to my team?",
  },
  {
    connectors: ["okta"],
    prompt: "Were there any suspicious *Okta* sign-ins overnight?",
  },
  {
    connectors: ["snowflake"],
    prompt: "Query *Snowflake* for this week’s signups by plan.",
  },
  {
    connectors: ["clickUp"],
    prompt: "What *ClickUp* tasks are due in my sprint this week?",
  },
  {
    connectors: ["mondayDotCom"],
    prompt: "What’s overdue on my *monday.com* boards?",
  },
  {
    connectors: ["trello"],
    prompt: "Which *Trello* cards are waiting in my review column?",
  },
  {
    connectors: ["github", "slack"],
    prompt:
      "Which *GitHub* pull requests are waiting on me, and did anyone nudge me about them in *Slack*?",
  },
  {
    connectors: ["jira", "github"],
    prompt:
      "Cross-reference my open *Jira* issues with recent *GitHub* commits.",
  },
  {
    connectors: ["salesforce", "zendesk"],
    prompt: "Which *Salesforce* accounts have open *Zendesk* tickets?",
  },
  {
    connectors: ["datadog", "pagerDuty"],
    prompt:
      "Correlate today’s *Datadog* alerts with the open *PagerDuty* incidents.",
  },
  {
    connectors: ["googleCalendar", "notion"],
    prompt:
      "Prep me for today’s *Google Calendar* meetings using notes from *Notion*.",
  },
  {
    connectors: ["slack"],
    prompt: "Draft a reply to my most recent *Slack* mention.",
  },
  {
    connectors: ["github"],
    prompt: "Draft a PR description for a *GitHub* branch I choose.",
  },
  {
    connectors: ["github"],
    prompt:
      "Write release notes from this week’s merged *GitHub* pull requests.",
  },
  {
    connectors: ["jira"],
    prompt: "Create a *Jira* ticket from a bug I’ll describe.",
  },
  {
    connectors: ["jira"],
    prompt: "Triage my unassigned *Jira* tickets and suggest priorities.",
  },
  {
    connectors: ["linear"],
    prompt: "Create a *Linear* issue and add it to my current cycle.",
  },
  {
    connectors: ["notion"],
    prompt: "Turn my rough notes into a tidy *Notion* doc.",
  },
  {
    connectors: ["googleCalendar"],
    prompt:
      "Find a free 30 minutes this week and draft a *Google Calendar* invite.",
  },
  {
    connectors: ["zendesk"],
    prompt: "Draft a response to my oldest open *Zendesk* ticket.",
  },
  {
    connectors: ["intercom"],
    prompt: "Draft a reply to the latest *Intercom* conversation in my inbox.",
  },
  {
    connectors: ["salesforce"],
    prompt: "Log a call summary on one of my *Salesforce* opportunities.",
  },
  {
    connectors: ["hubSpot"],
    prompt: "Draft a follow-up email to a *HubSpot* lead.",
  },
  {
    connectors: ["confluence"],
    prompt: "Draft a *Confluence* page from an outline I give you.",
  },
  {
    connectors: ["googleDocs"],
    prompt: "Proofread a *Google Docs* document and suggest edits.",
  },
  {
    connectors: ["googleSheets"],
    prompt:
      "Add this week’s totals as a summary row in my *Google Sheets* tracker.",
  },
  {
    connectors: ["stripe"],
    prompt: "Draft a recovery email for a failed *Stripe* payment.",
  },
  {
    connectors: ["asana"],
    prompt: "Break a project I describe into *Asana* tasks with due dates.",
  },
  {
    connectors: ["zoom"],
    prompt: "Turn an available *Zoom* recording transcript into action items.",
  },
  {
    connectors: ["sentry", "linear"],
    prompt:
      "Triage the newest *Sentry* error and draft a *Linear* issue for it.",
  },
  {
    connectors: ["datadog", "pagerDuty"],
    prompt:
      "Draft an incident summary from the latest *Datadog* alert and open *PagerDuty* incident.",
  },
];

export const chatExampleCatalog: Record<
  ChatExampleCategory,
  readonly ChatExample[]
> = {
  tasks,
  workflows,
  apps,
  monitoring,
  connectors: connectorExamples,
};
